'use client';

import { useState } from 'react';
import { RiGlobalLine } from '@remixicon/react';
import { motion } from 'motion/react';
import type { Variants } from 'motion/react';
import { useLocale, useTranslations } from 'next-intl';

import { Logo } from '@/components/logo';
import { Link, usePathname } from '@/i18n/navigation';
import { routing } from '@/i18n/routing';
import { dur, ease, sceneItem, spring, stagger } from '@/lib/motion';
import { siteLinks } from '@/lib/site-links';
import type { SiteLinkKey } from '@/lib/site-links';

/** Locale'i koruyan Link + Motion. Header'daki ile aynı kalıp. */
const MotionLink = motion.create(Link);

/**
 * Footer — sayfanın sonu.
 *
 * ---------------------------------------------------------------------------
 * Beyaz (kullanıcı kararı, 9 Ağu 2026)
 * ---------------------------------------------------------------------------
 * Footer önceden kapanış CTA'sı ile tek bir koyu gövdenin — `.dark-close` —
 * içinde saydam duruyordu. O kap kaldırıldı: koyu yüzey artık yalnızca CTA'nın
 * şeridi, footer beyaz ve kendi zeminini taşıyor. Sayfanın sonu "son bir
 * vurgu, sonra sessiz bir dizin" oluyor.
 *
 * Mürekkep tonları buna göre yeniden seçildi; hepsi beyaz üstünde en az 4.5:1
 * (globals.css → "Footer").
 *
 * Koyu şerit footer'ın üst kenarına TAM YAPIŞIK (kullanıcı kararı): aradaki
 * boşluğu footer'ın kendi üst dolgusu veriyor, ayrıca bir ayraç çizgisi ya da
 * dış boşluk yok.
 *
 * ---------------------------------------------------------------------------
 * Sütun hattı burada bitiyor
 * ---------------------------------------------------------------------------
 * Sayfanın ızgara dili 1px'lik dikey hatlar; CTA onları yukarıdan aşağı
 * çiziyor ve bilinçli olarak alt kenarı kapatmıyor ("sütun burada kapanmıyor,
 * footer'a devam ediyor" — globals.css → "Kapanış CTA"). Footer o hattı aynı
 * yönde, aynı eğriyle sürdürüp sayfanın son pikselinde bitiriyor. Bu yüzden
 * hatlar burada da `border` değil, çizilen `<motion.span>`.
 *
 * Hat CTA'nın koyu kartının değil, SÜTUNUN devamı: kart hattın içinde yüzüyor,
 * hattı taşımıyor. İkisi de artık açık zemin için siyah %12.
 *
 * ---------------------------------------------------------------------------
 * Ölü bağlantı yok
 * ---------------------------------------------------------------------------
 * Sütunlardaki sayfaların çoğu henüz açılmadı. Adres ve "sayfa var mı"
 * bilgisi `lib/site-links.ts`'te; burada karar verilmiyor, yalnızca
 * uygulanıyor. Yayında olan kayıt gerçek `<a>` basıyor; olmayan kayıt
 * tıklanamayan düz metin oluyor. Sayfa açıldığında tek değişiklik o dosyadaki
 * `live: true` — buraya dokunulmuyor.
 *
 * Durum satır satır DEĞİL, bir kez söyleniyor: sütunların altındaki tek
 * cümle. Denenip elenen sürüm her satıra "YAKINDA" etiketi koyuyordu —
 * on beş kalemin on beşi de açılmamış olduğu için footer bir rozet tarlasına
 * dönüyor, uzun adların yanında etiket alt satıra düşüp liste tırtıklı
 * duruyordu. Cümle, sayfa açıldıkça kendiliğinden küçülen bir bilgi:
 * hiç bekleyen kalmadığında hiç basılmıyor.
 *
 * Cümle görünüşe değil YAPIYA atıf yapıyor ("bağlantısı olmayan başlıklar"),
 * "soluk olanlar" demiyor: rengi görmeyen kullanıcı için de doğru kalsın.
 *
 * Şu an bu yüzden footer'daki tek gerçek bağlantı kelime işareti (anasayfa) ve
 * dil değiştirici. Geçici bir hâl, tasarım hatası değil.
 *
 * ---------------------------------------------------------------------------
 * Metin nereden geliyor
 * ---------------------------------------------------------------------------
 * Ürün ve Kaynaklar sütunlarının adları `header.menu.*` anahtarlarından — yani
 * header'ın mega menüsüyle BİREBİR aynı mesajdan — okunuyor. Kopyalansaydı iki
 * liste zamanla ayrışırdı; aynı sayfanın adı sitenin iki yerinde farklı olmaz.
 * Aynı gerekçeyle "Fiyatlandırma" da `header.pricing`.
 *
 * ---------------------------------------------------------------------------
 * Hareket neyi açıklıyor (DESIGN.md §4)
 * ---------------------------------------------------------------------------
 *   • Hatlar → CTA'dan gelen dikey çizgi inmeye devam ediyor ve burada
 *              duruyor. "Sayfa buraya kadar."
 *   • Giriş  → önce marka, sonra dört sütun sırayla odaklanıyor. Ölçülü
 *              taraftayız: burası bir kapanış, bir sahne değil.
 *   • Ayraç  → alt şeridin üstündeki hat soldan sağa çiziliyor; telif ve dil
 *              satırı ondan sonra geliyor.
 *   • Satır  → yalnızca GERÇEKTEN bir yere giden bağlantı hover'da 2px sağa
 *              kayıyor. Yayında olmayan satır kımıldamıyor — hareketin kendisi
 *              "bu tıklanır" bilgisi (globals.css).
 *   • Dil    → seçilinin zemini `layoutId` ile diğerine taşınıyor, üstelik
 *              tıklama anında: sayfa yüklenirken arayüz ölü durmuyor.
 */

type FooterLink = {
  /** `lib/site-links.ts` kaydı — adres ve yayında olup olmadığı orada. */
  route: SiteLinkKey;
  /** Kök namespace'ten tam mesaj anahtarı. */
  label: string;
};

type FooterColumn = {
  id: string;
  /** Sütun başlığının mesaj anahtarı; `<nav>`'ın erişilebilir adı da bu. */
  title: string;
  links: readonly FooterLink[];
};

const COLUMNS: readonly FooterColumn[] = [
  {
    id: 'product',
    title: 'footer.columns.product',
    links: [
      { route: 'featureAnalyze', label: 'header.menu.analyze.title' },
      { route: 'featureWorkflow', label: 'header.menu.workflow.title' },
      { route: 'featureAgent', label: 'header.menu.agent.title' },
      { route: 'featureCreative', label: 'header.menu.creative.title' },
    ],
  },
  {
    id: 'resources',
    title: 'footer.columns.resources',
    links: [
      { route: 'blog', label: 'header.menu.blog.title' },
      { route: 'docs', label: 'header.menu.docs.title' },
      { route: 'changelog', label: 'header.menu.changelog.title' },
      { route: 'help', label: 'header.menu.help.title' },
    ],
  },
  {
    id: 'company',
    title: 'footer.columns.company',
    links: [
      { route: 'about', label: 'footer.company.about' },
      { route: 'contact', label: 'footer.company.contact' },
      { route: 'pricing', label: 'header.pricing' },
    ],
  },
  {
    id: 'legal',
    title: 'footer.columns.legal',
    links: [
      { route: 'privacy', label: 'footer.legal.privacy' },
      { route: 'terms', label: 'footer.legal.terms' },
      { route: 'dataProtection', label: 'footer.legal.dataProtection' },
      { route: 'cookies', label: 'footer.legal.cookies' },
    ],
  },
];

/* Açılmamış sayfa kaldı mı? Kalmadıysa alttaki açıklama cümlesi hiç
   basılmıyor — footer o gün kendiliğinden sadeleşiyor. */
const HAS_PENDING = COLUMNS.some((col) => col.links.some(({ route }) => !siteLinks[route].live));

/** Dil kodu çevrilmez — "EN" her dilde EN. */
const LOCALE_LABEL: Record<(typeof routing.locales)[number], string> = {
  en: 'EN',
  tr: 'TR',
};

/* Önce hatlar, sonra içerik — cta.tsx ile aynı kurulum sırası. */
const frameV: Variants = {
  hidden: {},
  show: { transition: { staggerChildren: 0.08 } },
};

/**
 * Dikey hat yukarıdan aşağı iniyor. Değerler cta.tsx'teki `ruleV` ile BİREBİR
 * aynı olmak zorunda: bu iki çizgi görsel olarak tek bir hattın devamı, farklı
 * süre ya da eğri kullanılırsa sınırda hız değiştiren tek bir çizgi görünür.
 * Yay değil tween — çizilen hattın fiziksel karşılığı yok, spring'in aşımı ucu
 * hedefin altına taşırıp geri çekerdi.
 */
const ruleV: Variants = {
  hidden: { scaleY: 0, opacity: 0 },
  show: {
    scaleY: 1,
    opacity: 1,
    transition: {
      duration: dur.slow,
      ease: ease.out,
      opacity: { duration: dur.fast, ease: ease.out },
    },
  },
};

const contentV: Variants = {
  hidden: {},
  show: { transition: { staggerChildren: stagger.base, delayChildren: 0.06 } },
};

/** Alt şeridin ayracı — dikeylerin aksine SOLDAN SAĞA. */
const dividerV: Variants = {
  hidden: { scaleX: 0, opacity: 0 },
  show: {
    scaleX: 1,
    opacity: 1,
    transition: {
      duration: dur.slow,
      ease: ease.out,
      opacity: { duration: dur.fast, ease: ease.out },
    },
  },
};

export function SiteFooter({ year }: { year: number }) {
  /* Kök namespace: sütun adları hem `footer.*` hem `header.*` altından
     geliyor (yukarıdaki nota bak), tek namespace yetmiyor. */
  const t = useTranslations();

  return (
    <footer className="site-footer">
      {/* Sütun hero, CTA ve SSS ile aynı: .grid-item-8fr kendi `grid-column: 2`
          kuralıyla yerleşiyor, boş kardeşe gerek yok. */}
      <div className="grid-12-container">
        <div className="grid-item-8fr">
          <motion.div
            className="site-footer-frame"
            variants={frameV}
            initial={false}
            animate="show"
            /* amount düşük: footer uzun, 390px'te ekranın tamamından büyük.
               0.3 istenseydi kullanıcı sayfanın dibine varmadan hiçbir şey
               görünmezdi. */
          >
            <motion.span
              className="site-footer-rule site-footer-rule--l"
              variants={ruleV}
              aria-hidden="true"
            />
            <motion.span
              className="site-footer-rule site-footer-rule--r"
              variants={ruleV}
              aria-hidden="true"
            />

            <motion.div className="site-footer-content" variants={contentV}>
              <div className="site-footer-top">
                <motion.div className="site-footer-brand" variants={sceneItem}>
                  {/* Header'daki kilidin AYNISI (.header-logo) — footer'a
                      ikinci bir logo dili icat etmek markayı iki başlı
                      gösterir. Tek fark mürekkep rengi: kelime işareti
                      `currentColor`, açık zeminde koyuya dönüyor (CSS'te). */}
                  <MotionLink
                    href={siteLinks.home.href}
                    className="site-footer-logo-link"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.97 }}
                    transition={spring.snap}
                  >
                    <Logo className="header-logo" />
                  </MotionLink>
                  <p className="site-footer-tagline">{t('footer.tagline')}</p>
                </motion.div>

                {/* Her sütun kendi `<nav>` landmark'ı: ekran okuyucu footer'ı
                    tek bir bağlantı yığını olarak değil, adlandırılmış dört
                    bölüm olarak geziyor. Arama motoru site yapısını da buradan
                    çıkarıyor — bu yüzden yayındaki her hedef gerçek `<a>`. */}
                <div className="site-footer-navs">
                  <div className="site-footer-cols">
                    {COLUMNS.map((col) => (
                      <motion.nav
                        key={col.id}
                        className="site-footer-col"
                        aria-label={t(col.title)}
                        variants={sceneItem}
                      >
                        <h2 className="site-footer-col-title">{t(col.title)}</h2>
                        <ul className="site-footer-list">
                          {col.links.map(({ route, label }) => {
                            const target = siteLinks[route];

                            return (
                              <li key={route}>
                                {target.live ? (
                                  <Link href={target.href} className="site-footer-link">
                                    {t(label)}
                                  </Link>
                                ) : (
                                  /* Tıklanabilir DEĞİL: ne `<a>`, ne `href`,
                                     ne imleç. Neden tıklanmadığını sütunların
                                     altındaki tek cümle söylüyor. */
                                  <span className="site-footer-link site-footer-link--soon">
                                    {t(label)}
                                  </span>
                                )}
                              </li>
                            );
                          })}
                        </ul>
                      </motion.nav>
                    ))}
                  </div>

                  {HAS_PENDING ? (
                    <motion.p className="site-footer-note" variants={sceneItem}>
                      {t('footer.pendingNote')}
                    </motion.p>
                  ) : null}
                </div>
              </div>

              <motion.span className="site-footer-divider" variants={dividerV} aria-hidden="true" />

              <motion.div className="site-footer-bottom" variants={sceneItem}>
                <p className="site-footer-copy">{t('footer.copyright', { year: String(year) })}</p>
                <LanguageSwitch />
              </motion.div>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </footer>
  );
}

/**
 * Dil değiştirici — EN / TR.
 *
 * Site next-intl ile iki dilli ama arayüzde dili değiştirecek başka yer yok;
 * footer bunun doğal yeri.
 *
 * İki şey `<button>` yerine gerçek `<a>` olmasını gerektiriyor: JavaScript
 * gelmeden de çalışması ve arama motorunun iki dilli sürümü görebilmesi
 * (`hrefLang`). `href` olarak dil öneksiz MEVCUT yol veriliyor — öneki `Link`
 * koyuyor, yani /pricing'deyken dil değiştirmek sizi anasayfaya atmıyor.
 *
 * `pending`: tıklanan dil, sayfa daha yüklenmeden etkin gösteriliyor; zemin o
 * anda kayıyor. Yoksa gezinme tamamlanana kadar arayüz tıklamaya cevapsız
 * kalıyor. `aria-current` bilinçli olarak GERÇEK dile bağlı — görsel niyet ile
 * duyurulan durum ayrışsın istemiyoruz.
 */
function LanguageSwitch() {
  const t = useTranslations('footer');
  const locale = useLocale();
  const pathname = usePathname();
  const [pending, setPending] = useState<string | null>(null);
  const shown = pending ?? locale;

  return (
    <nav className="site-footer-lang" aria-label={t('language')}>
      <RiGlobalLine className="site-footer-lang-icon" size={16} aria-hidden="true" />
      <ul className="site-footer-lang-list">
        {routing.locales.map((code) => {
          const active = shown === code;

          return (
            <li key={code}>
              <Link
                href={pathname}
                locale={code}
                hrefLang={code}
                lang={code}
                className="site-footer-lang-link"
                data-active={active ? 'true' : 'false'}
                aria-current={locale === code ? 'true' : undefined}
                onClick={() => setPending(code)}
              >
                {/* Zemin tek parça: `layoutId` ile iki sekme arasında TAŞINIYOR,
                    her sekmede ayrı ayrı açılıp kapanmıyor. Hareket "seçim
                    öbürüne geçti" diyor. */}
                {active ? (
                  <motion.span
                    className="site-footer-lang-pill"
                    layoutId="footer-lang-pill"
                    transition={spring.snap}
                    aria-hidden="true"
                  />
                ) : null}
                <span className="site-footer-lang-code">{LOCALE_LABEL[code]}</span>
              </Link>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}
